import { useState } from "react";
import { X, FileText, Link as LinkIcon, Mail, Package, Clock, AlertCircle, User, Loader2 } from "lucide-react";
import { ChatStore } from "@/stores/ChatStore";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ClaimStatusOverlay() {
  const claimStatusInfo = ChatStore(state => state.claimStatusInfo);
  const setClaimStatusInfo = ChatStore(state => state.setClaimStatusInfo);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  if (!claimStatusInfo) {
    return null;
  }

  const handleClose = () => {
    setClaimStatusInfo(null);
  };

  const handleSendEmail = async () => {
    if (!claimStatusInfo.schadennummer) {
      toast.error("Schadennummer fehlt");
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await fetch("/api/multiadapter/claim-status/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schadennummer: claimStatusInfo.schadennummer }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        toast.error(data.error || data.message || "E-Mail-Versand fehlgeschlagen");
        return;
      }

      toast.success(data.message || "Status-Link wurde per E-Mail versendet");
    } catch (error) {
      console.error("[ClaimStatusOverlay] Email send error:", error);
      toast.error("E-Mail-Versand fehlgeschlagen");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleOpenStatusUrl = () => {
    if (claimStatusInfo.status_url) {
      window.open(claimStatusInfo.status_url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info("Status-Link ist noch nicht verfügbar");
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const hasPending = claimStatusInfo.pending && claimStatusInfo.pending.length > 0;
  const hasHistory = claimStatusInfo.history && claimStatusInfo.history.length > 0;

  return (
    <div className="overflow-hidden rounded-lg shadow-lg max-h-[80vh] overflow-y-auto">
      <div className="bg-white opacity-95 p-4 max-w-sm">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Ihr Schadenstatus
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          {/* Schadennummer */}
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Schadennummer</p>
              <p className="text-sm font-medium text-gray-900">{claimStatusInfo.schadennummer}</p>
            </div>
          </div>

          {/* Person Name */}
          {claimStatusInfo.person_name && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Versicherungsnehmer</p>
                <p className="text-sm font-medium text-gray-900">{claimStatusInfo.person_name}</p>
              </div>
            </div>
          )}

          {/* Produkt */}
          {claimStatusInfo.produkt_name && (
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Produkt</p>
                <p className="text-sm font-medium text-gray-900">{claimStatusInfo.produkt_name}</p>
              </div>
            </div>
          )}

          {/* VSNR */}
          {claimStatusInfo.vsnr && (
            <div>
              <p className="text-xs text-gray-500">Versicherungsscheinnummer</p>
              <p className="text-sm text-gray-900">{claimStatusInfo.vsnr}</p>
            </div>
          )}

          {/* Pending Items - Aktueller Status */}
          {hasPending && (
            <div className="bg-blue-50 rounded-md p-3 space-y-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <p className="text-xs font-medium text-blue-700">Aktueller Status</p>
              </div>
              {claimStatusInfo.pending!.map((item, index) => (
                <div key={index} className="pl-6">
                  <p className="text-sm font-medium text-blue-800">{item.description}</p>
                  {item.info && (
                    <p className="text-xs text-blue-600 mt-1">{item.info}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Fallback Status wenn keine pending items */}
          {!hasPending && claimStatusInfo.schaden_details?.status && (
            <div className="bg-blue-50 rounded-md p-2">
              <p className="text-xs text-blue-600">Aktueller Status</p>
              <p className="text-sm font-medium text-blue-800">
                {claimStatusInfo.schaden_details.status}
              </p>
            </div>
          )}

          {/* Status Text */}
          {!hasPending && claimStatusInfo.status_text && (
            <p className="text-sm text-gray-600 italic">
              {claimStatusInfo.status_text}
            </p>
          )}

          {/* History Items - Verlauf */}
          {hasHistory && (
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <p className="text-xs font-medium text-gray-700">Verlauf</p>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {claimStatusInfo.history!.slice().reverse().map((item, index) => (
                  <div key={index} className="pl-6 border-l-2 border-gray-200">
                    <p className="text-xs text-gray-500">{formatDate(item.timeline)}</p>
                    <p className="text-sm text-gray-700">{item.description}</p>
                    {item.info && (
                      <p className="text-xs text-gray-500 mt-0.5">{item.info}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 pt-2 border-t">
            {/* Status-URL Link */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenStatusUrl}
              className="w-full justify-start"
              disabled={!claimStatusInfo.status_url}
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              {claimStatusInfo.status_url ? "Status online ansehen" : "Link nicht verfügbar"}
            </Button>

            {/* E-Mail senden Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendEmail}
              className="w-full justify-start"
              disabled={isSendingEmail}
            >
              {isSendingEmail ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              {isSendingEmail ? "Wird gesendet..." : "Link per E-Mail senden"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
